
import { useMemo, useState, useEffect, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './MobilePromotionBanner.module.css';
import CountdownUnit from './CountdownUnit';
import Image from 'next/image';
import { usePromotionBannerLogic } from '@/hooks/usePromotionBannerLogic';
import { getStrapiURL } from '@/api/api';

const parseOfferText = (text: string, highlightClassName: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <span key={index} className={highlightClassName}>{part.slice(2, -2)}</span>;
        }
        return part;
    });
};

const enableAnimation = false;

export default function MobilePromotionBanner() {
    const { isVisible, timeLeft, handleClose, handleCopyCoupon, promotionBannerData } = usePromotionBannerLogic();
    const [showAnimatedBanner, setShowAnimatedBanner] = useState(false);

    useEffect(() => {
        if (!isVisible) {
            setShowAnimatedBanner(false);
            return;
        }
        const animatedBannerTimer = setTimeout(() => {
            setShowAnimatedBanner(true);
        }, 2000);
        return () => clearTimeout(animatedBannerTimer);
    }, [isVisible]);

    const formatDigit = (num: number) => num.toString().padStart(2, '0');

    const offerTextContent = useMemo(() => {
        if (promotionBannerData?.offer_text) {
            return promotionBannerData.offer_text.split('<br>').map((line, index, arr) => (
                <Fragment key={index}>
                    {parseOfferText(line, styles.offerHighlight)}
                    {index < arr.length - 1 && <br />}
                </Fragment>
            ));
        }
        return '';
    }, [promotionBannerData?.offer_text]);


    return (
        <AnimatePresence>
            {isVisible && promotionBannerData && (
                <motion.div
                    className={styles.bannerContainer}
                    initial={{ y: "100%", opacity: 0 }}
                    animate={{ y: "0%", opacity: 1 }}
                    exit={{ y: "100%", opacity: 0 }}
                    transition={{ duration: 0.8, ease: 'easeInOut' }}
                >
                    <div className={`${styles.mainSection} ${showAnimatedBanner ? styles.contentShifted : ''}`} style={{ backgroundColor: promotionBannerData.main_background_color }}>
                        <button onClick={handleClose} className={styles.closeButton} aria-label="Fechar banner">
                            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                                <path d="M7.15385 0.923105L1.00002 7.07693M1 0.92308L7.15385 7.07693" stroke="#939598" strokeWidth="0.984615" strokeLinecap="round" />
                            </svg>
                        </button>

                        <div className={styles.contentWrapper}>
                            <div className={styles.topRow}>
                                <div className={styles.couponLabelContainer}>
                                    {promotionBannerData.coupon_label.split('<br>').map((line, index) => (
                                        <p key={index} className={styles.couponLabel} style={{ color: promotionBannerData.text_color }}>{line}</p>
                                    ))}
                                </div>
                                <div className={styles.couponBox}>
                                    <div className={styles.couponBackground}>
                                        <Image src="/icons/coupon-background.svg" layout="fill" objectFit="fill" alt="Fundo do cupom" />
                                    </div>
                                    <span className={styles.couponCode} style={{ color: promotionBannerData.text_color }}>{promotionBannerData.coupon_code}</span>
                                    <button className={styles.copyButton} onClick={handleCopyCoupon} aria-label="Copiar cupom" />
                                </div>
                                <div className={styles.offerTextContainer}>
                                    <p className={styles.offerText} style={{ color: promotionBannerData.text_color }}>
                                        {offerTextContent}
                                    </p>
                                </div>
                            </div>

                            {(promotionBannerData.promotion_type === 'date_range' || promotionBannerData.countdown_duration_minutes > 0) && (
                                <div className={styles.countdownSection}>
                                    <CountdownUnit
                                        digit={formatDigit(timeLeft.days)}
                                        label="dias"
                                        styles={styles}
                                        digitColor={promotionBannerData.countdown_digit_color}
                                        labelColor={promotionBannerData.countdown_label_color}
                                        borderColor={promotionBannerData.countdown_border_color}
                                        backgroundColor={promotionBannerData.countdown_background_color}
                                    />
                                    <span className={styles.separator} style={{ color: promotionBannerData.countdown_separator_color }}>:</span>
                                    <CountdownUnit
                                        digit={formatDigit(timeLeft.hours)}
                                        label="horas"
                                        styles={styles}
                                        digitColor={promotionBannerData.countdown_digit_color}
                                        labelColor={promotionBannerData.countdown_label_color}
                                        borderColor={promotionBannerData.countdown_border_color}
                                        backgroundColor={promotionBannerData.countdown_background_color}
                                    />
                                    <span className={styles.separator} style={{ color: promotionBannerData.countdown_separator_color }}>:</span>
                                    <CountdownUnit
                                        digit={formatDigit(timeLeft.minutes)}
                                        label="minutos"
                                        styles={styles}
                                        digitColor={promotionBannerData.countdown_digit_color}
                                        labelColor={promotionBannerData.countdown_label_color}
                                        borderColor={promotionBannerData.countdown_border_color}
                                        backgroundColor={promotionBannerData.countdown_background_color}
                                    />
                                    <span className={styles.separator} style={{ color: promotionBannerData.countdown_separator_color }}>:</span>
                                    <CountdownUnit
                                        digit={formatDigit(timeLeft.seconds)}
                                        label="segundos"
                                        styles={styles}
                                        digitColor={promotionBannerData.countdown_digit_color}
                                        labelColor={promotionBannerData.countdown_label_color}
                                        borderColor={promotionBannerData.countdown_border_color}
                                        backgroundColor={promotionBannerData.countdown_background_color}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    {enableAnimation && (
                        <AnimatePresence>
                            {showAnimatedBanner && promotionBannerData.vehicle_image && (
                                <motion.div
                                    className={styles.animatedBannerSection}
                                    style={{ backgroundColor: promotionBannerData.animated_banner_background_color }}
                                    initial={{ y: "100%", opacity: 0 }}
                                    animate={{ y: "0%", opacity: 1 }}
                                    exit={{ y: "100%", opacity: 0 }}
                                    transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1], delay: 0.3 }}
                                >
                                    <div className={styles.carImageWrapper}>
                                        <Image src={getStrapiURL(promotionBannerData.vehicle_image.url)} alt={promotionBannerData.vehicle_image.alternativeText || "Carro"} layout="fill" objectFit="contain" />
                                    </div>
                                    <span className={styles.animatedBannerText} style={{ color: promotionBannerData.animated_banner_text_color }}>
                                        {promotionBannerData.animated_banner_text}
                                    </span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
}
